import { create } from 'zustand';
import { warningsApi, type MemberWarning } from '@/api/warnings';

export type { MemberWarning };

/**
 * The warnings an administrator sent to the signed-in member (FR-044,
 * data-model.md B1; mobile/src/state/warningsStore.ts). The unacknowledged
 * ones drive the WarningNotice; all of them are listed on the Warnings page.
 */
interface WarningsState {
  warnings: MemberWarning[];
  /** Loads the member's warnings. Never throws: offline keeps what is already known. */
  refresh: () => Promise<void>;
  acknowledge: (warningId: string) => Promise<void>;
  clear: () => void;
}

export const useWarningsStore = create<WarningsState>()((set, get) => ({
  warnings: [],

  refresh: async () => {
    try {
      set({ warnings: await warningsApi.list() });
    } catch {
      // keep the last known list
    }
  },

  acknowledge: async (warningId) => {
    const updated = await warningsApi.acknowledge(warningId);
    set({
      warnings: get().warnings.map((w) =>
        w.id === warningId ? { ...w, acknowledgedAt: updated.acknowledgedAt } : w,
      ),
    });
  },

  clear: () => set({ warnings: [] }),
}));

/** Unacknowledged warnings, oldest first (the order they should be read in). */
export function selectPending(state: Pick<WarningsState, 'warnings'>): MemberWarning[] {
  return state.warnings
    .filter((w) => !w.acknowledgedAt)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

const REFRESH_EVERY_MS = 60_000;

/**
 * While signed in, keeps the warnings fresh: at once, whenever the tab is shown
 * again and every minute, so a warning just sent appears without looking for it
 * (mobile useWarningsBootstrap). Returns a stop function.
 */
export function startWarningsSync(): () => void {
  const refresh = () => void useWarningsStore.getState().refresh();
  const onVisible = () => {
    if (document.visibilityState === 'visible') refresh();
  };
  refresh();
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(refresh, REFRESH_EVERY_MS);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}
